/**
 * LinkOverlay — SVG overlay rendering colored link lines between cards.
 *
 * Renders an absolutely-positioned SVG over the board scroll container.
 * Uses pointer-events: none so all board interactions pass through.
 * Queries card DOM positions via data-card-id attributes.
 *
 * Requirements: 3.1, 3.2, 3.3, 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 5.1, 5.2,
 *              5.3, 5.4, 5.5, 5.6, 6.1, 6.2, 6.3, 6.4, 6.5, 7.1, 7.3
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { LinkVisualizationState } from '../hooks/useLinkVisualization';
import { getLinkVisualAttributes, LINK_VISUAL_MAP, VisibleLink } from '../services/link-visual-attributes';
import { computeCenter, computeEdgeAnchor, clipToViewport, nearestEdge, spaceIndicators, Point, Rect } from '../services/link-geometry';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LinkOverlayProps {
  readonly state: LinkVisualizationState;
  readonly containerRef: React.RefObject<HTMLDivElement>;
}

interface RenderedPath {
  readonly linkId: string;
  readonly sourceCenter: Point;
  readonly endPoint: Point;
  readonly isClipped: boolean;
  readonly edge: 'top' | 'bottom' | 'left' | 'right' | null;
  readonly color: string;
  readonly dashArray: string;
  readonly depth: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STROKE_WIDTH = 2;
const STROKE_OPACITY = 0.8;
const STROKE_OPACITY_SECOND_HOP = 0.4;
const INDICATOR_SIZE = 12;
const INDICATOR_MIN_GAP = 8;
const FADE_DURATION_MS = 300;
const Z_INDEX_OVERLAY = 30; // Above cards (z-10..20), below modals (z-50)

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getCardRect(cardId: string, containerRect: DOMRect): Rect | null {
  const el = document.querySelector(`[data-card-id="${cardId}"]`);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  return {
    x: rect.left - containerRect.left,
    y: rect.top - containerRect.top,
    width: rect.width,
    height: rect.height,
  };
}

function getViewportRect(containerEl: HTMLDivElement): Rect {
  return {
    x: containerEl.scrollLeft,
    y: containerEl.scrollTop,
    width: containerEl.clientWidth,
    height: containerEl.clientHeight,
  };
}

function buildPathD(from: Point, to: Point): string {
  return `M ${from.x} ${from.y} L ${to.x} ${to.y}`;
}

function buildIndicatorPath(point: Point, edge: 'top' | 'bottom' | 'left' | 'right'): string {
  const half = INDICATOR_SIZE / 2;
  // Draw a chevron pointing in the direction of the off-screen card
  switch (edge) {
    case 'top':
      return `M ${point.x - half} ${point.y + half} L ${point.x} ${point.y} L ${point.x + half} ${point.y + half}`;
    case 'bottom':
      return `M ${point.x - half} ${point.y - half} L ${point.x} ${point.y} L ${point.x + half} ${point.y - half}`;
    case 'left':
      return `M ${point.x + half} ${point.y - half} L ${point.x} ${point.y} L ${point.x + half} ${point.y + half}`;
    case 'right':
      return `M ${point.x - half} ${point.y - half} L ${point.x} ${point.y} L ${point.x - half} ${point.y + half}`;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LinkOverlay({ state, containerRef }: LinkOverlayProps): React.ReactElement | null {
  const { activeCardId, links, isFadingOut } = state;
  const [paths, setPaths] = useState<RenderedPath[]>([]);
  const [svgSize, setSvgSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const recalcTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Compute paths from current state and DOM positions
  const computePaths = useCallback((): RenderedPath[] => {
    const container = containerRef.current;
    if (!container || !activeCardId || links.length === 0) return [];

    const containerRect = container.getBoundingClientRect();
    const viewport = getViewportRect(container);

    const sourceRect = getCardRect(activeCardId, containerRect);
    if (!sourceRect) return [];

    const computed: RenderedPath[] = [];

    for (const link of links) {
      const targetRect = getCardRect(link.targetCardId, containerRect);
      if (!targetRect) continue;

      // Determine the source for this link line:
      // - depth 0 (direct links): source is the active card
      // - depth 1 (second-hop): source is the intermediate card
      const depth = link.depth ?? 0;
      const linkSourceRect = depth === 0
        ? sourceRect
        : (link.sourceCardId ? getCardRect(link.sourceCardId, containerRect) : null);

      if (!linkSourceRect) continue;

      const targetCenter = computeCenter(targetRect);
      const linkSourceCenter = computeCenter(linkSourceRect);

      // Compute edge anchor points
      const sourceAnchor = computeEdgeAnchor(linkSourceRect, targetCenter);
      const targetAnchor = computeEdgeAnchor(targetRect, linkSourceCenter);

      const { point: endPoint, isClipped } = clipToViewport(sourceAnchor, targetAnchor, viewport);
      const visual = getLinkVisualAttributes(link.linkType);
      const edge = isClipped ? nearestEdge(endPoint, viewport) : null;

      computed.push({
        linkId: link.linkId,
        sourceCenter: sourceAnchor,
        endPoint,
        isClipped,
        edge,
        color: visual.color,
        dashArray: visual.dashArray,
        depth,
      });
    }

    return computed;
  }, [activeCardId, links, containerRef]);

  // Recalculate positions and SVG size
  const recalculate = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    setSvgSize({
      width: container.scrollWidth,
      height: container.scrollHeight,
    });
    setPaths(computePaths());
  }, [computePaths, containerRef]);

  // Recalculate when state changes
  useEffect(() => {
    recalculate();
  }, [recalculate]);

  // Handle scroll events to update endpoints
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !activeCardId || links.length === 0) return;

    const handleScroll = () => {
      setPaths(computePaths());
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [activeCardId, links, computePaths, containerRef]);

  // Handle resize via ResizeObserver — recalculate within 200ms
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    resizeObserverRef.current = new ResizeObserver(() => {
      if (recalcTimeoutRef.current !== null) {
        clearTimeout(recalcTimeoutRef.current);
      }
      recalcTimeoutRef.current = setTimeout(() => {
        recalculate();
        recalcTimeoutRef.current = null;
      }, 100); // Respond within 200ms budget
    });

    resizeObserverRef.current.observe(container);

    return () => {
      resizeObserverRef.current?.disconnect();
      if (recalcTimeoutRef.current !== null) {
        clearTimeout(recalcTimeoutRef.current);
      }
    };
  }, [containerRef, recalculate]);

  // Nothing to render if idle
  if (!activeCardId || links.length === 0) return null;

  // Space indicators on the same edge
  const indicatorsByEdge = new Map<string, { point: Point; path: RenderedPath }[]>();
  for (const p of paths) {
    if (p.isClipped && p.edge) {
      const existing = indicatorsByEdge.get(p.edge);
      if (existing) {
        existing.push({ point: p.endPoint, path: p });
      } else {
        indicatorsByEdge.set(p.edge, [{ point: p.endPoint, path: p }]);
      }
    }
  }

  const spacedIndicators: { point: Point; path: RenderedPath }[] = [];
  for (const [edge, items] of indicatorsByEdge.entries()) {
    const rawPoints = items.map((i) => i.point);
    const spaced = spaceIndicators(rawPoints, edge as 'top' | 'bottom' | 'left' | 'right', INDICATOR_MIN_GAP);
    for (let i = 0; i < spaced.length; i++) {
      const spacedPoint = spaced[i];
      const item = items[i];
      if (spacedPoint && item) {
        spacedIndicators.push({ point: spacedPoint, path: item.path });
      }
    }
  }

  // Determine which link types are active for the legend
  const activeLinkTypes = new Set(links.map((l) => l.linkType));

  return (
    <svg
      data-testid="link-overlay"
      aria-hidden="true"
      className="absolute top-0 left-0"
      style={{
        width: svgSize.width,
        height: svgSize.height,
        pointerEvents: 'none',
        zIndex: Z_INDEX_OVERLAY,
        opacity: isFadingOut ? 0 : 1,
        transition: `opacity ${FADE_DURATION_MS}ms ease-out`,
      }}
    >
      {/* Link lines */}
      {paths.map((p) => (
        <path
          key={p.linkId}
          d={buildPathD(p.sourceCenter, p.endPoint)}
          fill="none"
          stroke={p.color}
          strokeWidth={STROKE_WIDTH}
          strokeDasharray={p.dashArray === '0' ? undefined : p.dashArray}
          opacity={p.depth === 0 ? STROKE_OPACITY : STROKE_OPACITY_SECOND_HOP}
        />
      ))}

      {/* Direction indicators for off-screen cards */}
      {spacedIndicators.map(({ point, path: p }) => (
        <path
          key={`indicator-${p.linkId}`}
          d={buildIndicatorPath(point, p.edge!)}
          fill="none"
          stroke={p.color}
          strokeWidth={STROKE_WIDTH}
          strokeDasharray={p.dashArray === '0' ? undefined : p.dashArray}
          opacity={STROKE_OPACITY}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}

      {/* Legend — positioned in bottom-right corner */}
      <foreignObject
        x={svgSize.width - 160}
        y={svgSize.height - 30 - activeLinkTypes.size * 24}
        width={150}
        height={20 + activeLinkTypes.size * 24}
        style={{ pointerEvents: 'none' }}
      >
        <div
          className="bg-white/90 dark:bg-gray-800/90 rounded px-2 py-1 text-xs shadow border border-gray-200 dark:border-gray-700"
        >
          {(Object.entries(LINK_VISUAL_MAP) as [string, { color: string; dashArray: string; label: string }][])
            .filter(([type]) => activeLinkTypes.has(type as VisibleLink['linkType']))
            .map(([type, attrs]) => (
              <div key={type} className="flex items-center gap-2 py-0.5">
                <svg width="20" height="10" className="flex-shrink-0">
                  <line
                    x1="0"
                    y1="5"
                    x2="20"
                    y2="5"
                    stroke={attrs.color}
                    strokeWidth={STROKE_WIDTH}
                    strokeDasharray={attrs.dashArray === '0' ? undefined : attrs.dashArray}
                    opacity={STROKE_OPACITY}
                  />
                </svg>
                <span className="text-gray-700 dark:text-gray-300">{attrs.label}</span>
              </div>
            ))}
        </div>
      </foreignObject>
    </svg>
  );
}
